/**
 * Telemetry — browser-side OpenTelemetry via @morezero/telemetry.
 *
 * Initialises the Web TracerProvider with OTLP HTTP export, fetch
 * auto-instrumentation, document-load and user-interaction tracing.
 *
 * The logger facade is retained for backward compatibility with existing
 * callers throughout admin-ui.
 *
 * Log level: set VITE_LOG_LEVEL or LOG_LEVEL (error | warn | info | debug). Default: info.
 */

import { startWebTelemetry } from '@morezero/telemetry/web';

const LOG_LEVEL_ORDER = ['error', 'warn', 'info', 'debug'] as const;
type LogLevel = (typeof LOG_LEVEL_ORDER)[number];

function getMinLevel(): LogLevel {
    const raw =
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_LOG_LEVEL) ||
        (typeof process !== 'undefined' && process.env?.LOG_LEVEL) ||
        'info';
    const level = String(raw).toLowerCase();
    return LOG_LEVEL_ORDER.includes(level as LogLevel) ? (level as LogLevel) : 'info';
}

function isEnabled(minLevel: LogLevel, current: LogLevel): boolean {
    const minIdx = LOG_LEVEL_ORDER.indexOf(minLevel);
    const curIdx = LOG_LEVEL_ORDER.indexOf(current);
    return curIdx >= 0 && curIdx <= minIdx;
}

const minLevel = getMinLevel();

export const logger = {
    debug: (...args: unknown[]) => {
        if (isEnabled(minLevel, 'debug')) console.debug('[telemetry]', ...args);
    },
    info: (...args: unknown[]) => {
        if (isEnabled(minLevel, 'info')) console.info('[telemetry]', ...args);
    },
    warn: (...args: unknown[]) => {
        if (isEnabled(minLevel, 'warn')) console.warn('[telemetry]', ...args);
    },
    error: (...args: unknown[]) => {
        if (isEnabled(minLevel, 'error')) console.error('[telemetry]', ...args);
    },
};

const otelEnabled = (import.meta as any).env?.VITE_OTEL_ENABLED === 'true';

if (otelEnabled && typeof window !== 'undefined') {
    const traceEndpoint = (import.meta as any).env?.VITE_OTEL_TRACES_URL;
    startWebTelemetry({
        serviceName: 'admin-ui',
        serviceVersion: (import.meta as any).env?.VITE_APP_VERSION ?? '0.1.0',
        traceEndpoint,
        corsUrls: /.*/,
    }).catch((err: unknown) => {
        logger.warn('Failed to initialise browser telemetry:', err);
    });
}
